import React, { useState } from 'react';
import { AVAILABLE_SAMPLES, SampleTopology, loadSampleTopology } from '../utils/sampleDataLoader';
import { useVisualizationStore } from '../store/visualizationStore';

interface SampleTopologySelectorProps {
  category?: 'hierarchical' | 'ring' | 'dragonfly' | 'torus';
  onLoadStart?: () => void;
  onLoadComplete?: () => void;
  onLoadError?: (error: string) => void;
}

export const SampleTopologySelector: React.FC<SampleTopologySelectorProps> = ({
  category,
  onLoadStart,
  onLoadComplete,
  onLoadError
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [loadingTopology, setLoadingTopology] = useState<string | null>(null);
  const { loadInfrastructure } = useVisualizationStore();

  const handleLoadTopology = async (topology: SampleTopology) => {
    try {
      setLoadingTopology(topology.id);
      onLoadStart?.();

      const data = await loadSampleTopology(topology.id);
      loadInfrastructure(data);
      
      onLoadComplete?.();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load topology';
      console.error(`Failed to load ${topology.name}:`, error);
      onLoadError?.(errorMessage);
    } finally {
      setLoadingTopology(null);
    }
  };


  const getComplexityColor = (complexity: SampleTopology['complexity']) => {
    switch (complexity) {
      case 'simple': return 'bg-emerald-100 text-emerald-700';
      case 'medium': return 'bg-yellow-100 text-yellow-700';
      case 'complex': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };


  // Filter samples by category if specified
  const filteredSamples = category 
    ? AVAILABLE_SAMPLES.filter(sample => sample.category === category)
    : AVAILABLE_SAMPLES;

  return (
    <div className="w-full">
      <div className="text-center mb-4">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium text-sm w-full justify-center"
        >
          Browse {filteredSamples.length} Topolog{filteredSamples.length !== 1 ? 'ies' : 'y'}
          <span className={`ml-2 transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}>
            ▼
          </span>
        </button>
        
      </div>

      {isExpanded && (
        <div className="bg-gray-50 rounded-lg p-4 border">
          <div className="space-y-3">
            {filteredSamples.map((topology) => (
              <div key={topology.id} className="bg-white rounded border p-3">
                <div className="flex justify-between items-center mb-2">
                  <h5 className="font-medium text-gray-900 text-sm">{topology.name}</h5>
                  <div className="flex space-x-1">
                    <span className={`px-1.5 py-0.5 rounded text-xs font-medium flex items-center ${getComplexityColor(topology.complexity)}`}>
                      {topology.complexity}
                    </span>
                    <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600 flex items-center">
                      {topology.nodeCount} nodes
                    </span>
                  </div>
                </div>
                
                <p className="text-gray-600 text-xs mb-3">
                  {topology.description}
                </p>

                <button
                  onClick={() => handleLoadTopology(topology)}
                  disabled={loadingTopology === topology.id}
                  className="w-full px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors text-xs font-medium"
                >
                  {loadingTopology === topology.id ? (
                    <div className="flex items-center justify-center space-x-2">
                      <div className="animate-spin w-3 h-3 border-2 border-white border-t-transparent rounded-full"></div>
                      <span>Loading...</span>
                    </div>
                  ) : (
                    `Load ${topology.name}`
                  )}
                </button>
              </div>
            ))}
          </div>

          <div className="mt-3 text-center">
            <button
              onClick={() => setIsExpanded(false)}
              className="text-gray-500 hover:text-gray-700 transition-colors text-xs"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};